﻿import {translation} from "@2d-front-monorepo/core";
import {
	useInitializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect
} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useMemo} from "react";
import DriverPropertyFlags from "../enums/DriverPropertyFlags";
import useCreateSelectItems from "../helpers/useCreateSelectItems";
import {useScrollToFirstError} from "../helpers/scrollToFirstError";
import selectOrCreate from "../helpers/selectOrCreate";
import useEntityAttachments from "../helpers/useEntityAttachments";
import {DriverComponent} from "../index";
import {DriverId} from "../nominalTypes/DriverId";
import {DriverResult} from "../types/DriverResult";
import {EntityProfile} from "../types/InstallationByHostData";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateDriverComponent, {CreateDriverModel} from "./CreateDriverComponent";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";

export interface CreateOrSelectDriverComponentProps extends ComponentParts<CreateOrSelectDriverModel> {
	autoFocus?: boolean;
	configuration: EntityProfile<DriverPropertyFlags>;
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectDriverModel>>;
	createEnabled?: boolean;
	createParts?: ComponentWithChildrenParts<CreateDriverModel>;
	drivers?: DriverResult[];
	filesPrefix: string;
	initialState: CreateOrSelectDriverModel;
	onSubmit: (formData: CreateOrSelectDriverModel) => Promise<void>;
	selectParts?: ComponentWithChildrenParts<CreateOrSelectDriverModel>;
}

export interface CreateOrSelectDriverModel extends CreateDriverModel {
	driverId: DriverId;
}

export default function CreateOrSelectDriverComponent(props: CreateOrSelectDriverComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		filesPrefix,
		drivers,
		contextRef,
		header,
		footer,
		configuration,
		createEnabled,
		createParts,
		selectParts
	} = props;

	const choose = useCreateSelectItems(drivers, createEnabled, x => x.fullName);
	const multiple = choose.length > 1;

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			initialState.driverId = selectOrCreate(choose, initialState.driverId);
			return initialState;
		},
		contextRef: contextRef
	});

	const state = context.state;
	const selectedDriver = useMemo(() => drivers?.find(r => r.id === state.driverId), [drivers, state.driverId]);
	const attachments = useEntityAttachments(selectedDriver, configuration.attachments);
	const createVisible = createEnabled && state.driverId === "create";
	const formRef = useScrollToFirstError(context);

	return <MvvmDataProvider<CreateOrSelectDriverModel> onSubmit={onSubmit} context={context} ref={formRef}>
		<PermitsMvvmErrorCollector/>
		{header?.(context.state)}

		<div className="layout vertical gap">
			{multiple && <MvvmSelect context={context}
                                     property="driverId"
                                     label={translation(PermitTranslations.new_driver__choose)}
                                     items={choose}/>}

			{selectedDriver && <>
				{selectParts?.header?.(context.state)}
                <DriverComponent driver={selectedDriver} filesPrefix={filesPrefix}/>
				{selectParts?.children}
                <FullCreateAttachmentsComponent context={context} attachments={attachments}/>

				{selectParts?.footer?.(context.state)}
            </>}

			{createVisible &&
                <CreateDriverComponent context={context}
                                       configuration={configuration}
                                       header={createParts?.header}
                                       children={createParts?.children}
                                       footer={createParts?.footer}/>
			}
		</div>
		{footer?.(context.state)}
	</MvvmDataProvider>;
}
